// src/admin/pages/UserListPage.jsx
// v206: an account a purge anonymised (…@purged.invalid) shows «Account eliminato» and no action;
// failures are worded from err.code (admin/adminErrorCopy.js).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPlus, Search } from 'lucide-react';
import AdminTable from '../components/AdminTable.jsx';
import AdminPagination from '../components/AdminPagination.jsx';
import CreateUserModal from './CreateUserModal.jsx';
import { fetchUsers, activateUser, deactivateUser } from '../adminApi.js';
import { isPurgedAccount } from '../purge/purgeModel.js';
import { adminErrorText } from '../adminErrorCopy.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const LIMIT = 20;

export default function UserListPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchUsers({ limit: LIMIT, offset, email: emailFilter || undefined });
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(adminErrorText(t, err));
    } finally {
      setLoading(false);
    }
  }, [offset, emailFilter, t]);

  // Reset pagination offset when the email filter changes to avoid showing a
  // stale offset (e.g. offset=40 after a new search that yields 3 results).
  useEffect(() => { setOffset(0); }, [emailFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  async function handleToggleActive(user) {
    try {
      if (user.is_active) {
        await deactivateUser(user.user_id);
      } else {
        await activateUser(user.user_id);
      }
      loadUsers();
    } catch (err) {
      alert(adminErrorText(t, err));
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    setOffset(0);
  }

  const columns = [
    { key: 'email', label: t('shared.email') },
    {
      key: 'name', label: t('shared.name'), width: '180px',
      // Name is nullable; show a dash rather than repeating the email column.
      render: (row) => {
        const name = [row.first_name, row.last_name].filter(Boolean).join(' ');
        return name || '—';
      },
    },
    {
      key: 'is_active', label: t('shared.status'), width: '100px',
      render: (row) => (isPurgedAccount(row) ? (
        <span style={{ padding: '2px 8px', borderRadius: RADII.controlInner, background: COLORS.borderStrong, color: COLORS.surface, fontSize: '11px', fontWeight: 600 }}>
          {t('purge.person.deletedMarker')}
        </span>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: RADII.pill,
            background: row.is_active ? SEMANTIC.verde.fg : SEMANTIC.errore.fg,
            display: 'inline-block',
          }} />
          {row.is_active ? t('shared.active') : t('shared.inactive')}
        </span>
      )),
    },
    {
      key: 'is_superadmin', label: t('users.columns.superadmin'), width: '100px',
      render: (row) => row.is_superadmin ? (
        <span style={{ padding: '2px 8px', borderRadius: RADII.controlInner, background: COLORS.accentSoft, color: COLORS.accent, fontSize: '11px', fontWeight: 600 }}>
          SA
        </span>
      ) : null,
    },
    {
      key: 'created_at', label: t('users.columns.createdAt'), width: '140px',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString('it-IT') : '—',
    },
    {
      key: 'actions', label: '', width: '120px',
      render: (row) => (isPurgedAccount(row) ? null : (
        <button
          onClick={(e) => { e.stopPropagation(); handleToggleActive(row); }}
          style={{
            padding: '4px 10px', borderRadius: RADII.controlInner, fontSize: '11px', cursor: 'pointer',
            border: `1px solid ${row.is_active ? SEMANTIC.errore.bg : SEMANTIC.verde.bg}`,
            background: row.is_active ? SEMANTIC.errore.bg : SEMANTIC.verde.bg,
            color: row.is_active ? SEMANTIC.errore.fg : SEMANTIC.verde.fg,
            fontFamily: FONT_SANS,
          }}
        >
          {row.is_active ? t('users.deactivate') : t('users.activate')}
        </button>
      )),
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1100px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{t('users.title')}</h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '4px 0 0' }}>{t('users.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: RADII.controlInner, border: 'none',
            background: COLORS.accent, color: COLORS.surface, fontSize: '13px',
            fontWeight: 600, cursor: 'pointer', fontFamily: FONT_SANS,
          }}
        >
          <UserPlus size={15} /> {t('users.create')}
        </button>
      </div>

      <form onSubmit={handleSearch} style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative', maxWidth: '320px' }}>
          <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
          <input
            type="text"
            placeholder={t('users.searchPlaceholder')}
            value={emailFilter}
            onChange={e => setEmailFilter(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 36px', borderRadius: RADII.controlInner,
              border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
              fontSize: '13px', boxSizing: 'border-box', outline: 'none',
              fontFamily: FONT_SANS,
            }}
          />
        </div>
      </form>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <AdminTable
          columns={columns}
          rows={users}
          loading={loading}
          emptyMessage={t('users.empty')}
          onRowClick={(row) => navigate(`/admin/users/${row.user_id}`)}
        />
      </div>

      <AdminPagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={loadUsers} />
    </div>
  );
}
